/**
 * The one copy of who is signed in, handed down the route tree.
 *
 * Every screen that needs the caller reads it from here rather than calling
 * `useMe` again, which is what lets a write on the account screen reach the
 * sidebar's card in the same render: `replaceMe` puts the server's answer into
 * the one place everything reads. The provider that fills this context is a
 * component in its own file, so fast refresh can reload it without dropping
 * the session.
 *
 * `me` spins for ever without a user id. A public screen — the viewing room —
 * takes `session` and `loading` from here and must never branch on `me`.
 */

import type { MeResponse } from "@ondo/shared";
import type { Session } from "@ondo/supabase/browser";
import { createContext, useContext } from "react";

import type { MeState } from "./useMe.ts";

export interface Account {
  session: Session | null;
  /** True until the initial session lookup resolves; until then no route can decide anything. */
  loading: boolean;
  me: MeState;
  /**
   * Hands back an account the server has just returned, so one screen's write
   * is visible on every other. See `useMe` for why this is a replace and not a
   * refetch.
   */
  replaceMe: (me: MeResponse) => void;
  signOut: () => void;
}

export const AccountContext = createContext<Account | null>(null);

/**
 * The caller, for any route that needs to know who is asking.
 *
 * Separate from the provider so that file exports only a component — the rule
 * fast refresh needs to hot-reload it without dropping the session.
 */
export function useAccount(): Account {
  const account = useContext(AccountContext);
  if (!account) throw new Error("useAccount must be used inside an AccountProvider");

  return account;
}
